// Application configuration. Typed wrapper around `import.meta.env` that
// narrows types, fills sensible defaults, and fails fast on missing required
// vars so a misconfigured deploy crashes bootstrap instead of producing a
// half-working app.
//
// Feature code reads from `config` exclusively — never `import.meta.env`
// directly, never `process.env`. Add a new key here when a new VITE_* env var
// is introduced, document it in `.env.example`, then surface it on `config`.

export type AppEnv = 'development' | 'qa' | 'uat' | 'staging' | 'production' | 'test'
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent'

interface RawEnv {
  // platform-injected (vite)
  MODE: string
  PROD: boolean
  DEV: boolean

  // app-level
  VITE_APP_ENV?: string
  VITE_LOG_LEVEL?: string

  // api
  VITE_AUTH_API_URL?: string
  VITE_INVESTOR_API_URL?: string
  VITE_SLICEY_API_URL?: string

  // monitoring — sentry
  VITE_SENTRY_DSN?: string
  VITE_SENTRY_ENVIRONMENT?: string
  VITE_SENTRY_RELEASE?: string
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string

  // monitoring — mixpanel
  VITE_MIXPANEL_TOKEN?: string
  VITE_MIXPANEL_API_HOST?: string

  // monitoring — web vitals
  VITE_ENABLE_WEB_VITALS?: string

  // observability — OpenTelemetry browser-facing OTLP collector
  VITE_OTEL_OTLP_ENDPOINT?: string
  VITE_OTEL_EXPORTER_OTLP_ENDPOINT?: string
  VITE_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT?: string
  VITE_OTEL_EXPORTER_OTLP_METRICS_ENDPOINT?: string
  VITE_OTEL_EXPORTER_OTLP_LOGS_ENDPOINT?: string
  VITE_OTEL_SERVICE_NAME?: string
  VITE_OTEL_METRIC_EXPORT_INTERVAL_MS?: string
  VITE_OTEL_ENABLED?: string
}

const raw = import.meta.env as unknown as RawEnv

const PROD_TRACES_SAMPLE_RATE = 0.1
const NON_PROD_TRACES_SAMPLE_RATE = 1.0
const DEFAULT_OTEL_METRIC_EXPORT_INTERVAL_MS = 60_000

const required = (name: keyof RawEnv): string => {
  const value = raw[name]
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`[config] missing required env var ${String(name)}`)
  }
  return value
}

const optional = (name: keyof RawEnv, fallback = ''): string => {
  const value = raw[name]
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

const optionalFloat = (name: keyof RawEnv, fallback: number): number => {
  const value = raw[name]
  if (typeof value !== 'string' || value.length === 0) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const optionalInt = (name: keyof RawEnv, fallback: number): number => {
  const value = raw[name]
  if (typeof value !== 'string' || value.length === 0) return fallback
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const optionalBool = (name: keyof RawEnv, fallback: boolean): boolean => {
  const value = raw[name]
  if (typeof value !== 'string' || value.length === 0) return fallback
  return value === 'true' || value === '1'
}

const appEnv: AppEnv = (() => {
  const envValue = optional('VITE_APP_ENV', raw.MODE)
  if (
    envValue === 'development' ||
    envValue === 'qa' ||
    envValue === 'uat' ||
    envValue === 'staging' ||
    envValue === 'production' ||
    envValue === 'test'
  ) {
    return envValue
  }
  return raw.PROD ? 'production' : 'development'
})()

// Default log level by environment. Production stays at `info` so Sentry
// breadcrumbs carry useful context before an error fires; flip via
// VITE_LOG_LEVEL when noise needs to be reduced.
const defaultLogLevel: LogLevel = (() => {
  switch (appEnv) {
    case 'production':
    case 'staging':
    case 'uat':
    case 'qa':
      return 'info'
    case 'test':
      return 'silent'
    default:
      return 'debug'
  }
})()

const parseLogLevel = (raw: string | undefined, fallback: LogLevel): LogLevel => {
  if (!raw) return fallback
  if (raw === 'debug' || raw === 'info' || raw === 'warn' || raw === 'error' || raw === 'silent') {
    return raw
  }
  return fallback
}

export const config = {
  appEnv,
  isProd: appEnv === 'production',
  isDev: appEnv === 'development',
  isStaging: appEnv === 'staging',
  isQa: appEnv === 'qa',
  isUat: appEnv === 'uat',
  isTest: appEnv === 'test',

  // Build-time service identity (vite `define` from package.json). Rides on
  // every log record + Sentry event + Mixpanel super-property so frontend
  // events can be joined with backend traces on (service, version).
  app: {
    name: __APP_NAME__,
    version: __APP_VERSION__,
  },

  logLevel: parseLogLevel(optional('VITE_LOG_LEVEL'), defaultLogLevel),

  // API
  authApiUrl: required('VITE_AUTH_API_URL'),
  investorApiUrl: required('VITE_INVESTOR_API_URL'),
  // Slicey AI conversation service. Optional — when missing, the Slicey
  // mutation surfaces a clean error to the user. Required in prod deploys.
  sliceyApiUrl: optional('VITE_SLICEY_API_URL'),

  // Sentry — DSN required only in non-dev environments. The bootstrap step
  // short-circuits if missing so dev/CI runs don't try to phone home.
  // Production samples 10% of transactions; non-prod samples 100% so dev
  // signal is dense while prod stays inside the Sentry quota.
  sentry: {
    dsn: optional('VITE_SENTRY_DSN'),
    environment: optional('VITE_SENTRY_ENVIRONMENT', appEnv),
    release: optional('VITE_SENTRY_RELEASE'),
    tracesSampleRate: optionalFloat(
      'VITE_SENTRY_TRACES_SAMPLE_RATE',
      appEnv === 'production' ? PROD_TRACES_SAMPLE_RATE : NON_PROD_TRACES_SAMPLE_RATE,
    ),
  },

  // Mixpanel — token absent ⇒ analytics surface no-ops cleanly. Useful for
  // dev/CI so we don't pollute the prod project.
  mixpanel: {
    token: optional('VITE_MIXPANEL_TOKEN'),
    apiHost: optional('VITE_MIXPANEL_API_HOST'),
  },

  webVitals: {
    enabled: optionalBool('VITE_ENABLE_WEB_VITALS', appEnv !== 'development'),
  },

  // OpenTelemetry — browser-facing OTLP collector. Optional; when no endpoint
  // is configured OTel init skips registration so the app runs without a
  // collector. Three signals are emitted: traces, metrics (incl. Web Vitals
  // histograms), logs — all via OTLP/HTTP.
  //
  // Endpoint resolution: a single base URL (`VITE_OTEL_EXPORTER_OTLP_ENDPOINT`
  // or the legacy `VITE_OTEL_OTLP_ENDPOINT`) is suffixed with `/v1/traces`,
  // `/v1/metrics`, `/v1/logs` per signal. Per-signal overrides take
  // precedence when present.
  otel: {
    enabled: optionalBool(
      'VITE_OTEL_ENABLED',
      optional('VITE_OTEL_EXPORTER_OTLP_ENDPOINT').length > 0 ||
        optional('VITE_OTEL_OTLP_ENDPOINT').length > 0,
    ),
    // Legacy alias kept for backwards-compatibility; otel.ts derives the
    // effective base from `baseEndpoint` (preferring the new var when both set).
    otlpEndpoint: optional('VITE_OTEL_OTLP_ENDPOINT'),
    baseEndpoint:
      optional('VITE_OTEL_EXPORTER_OTLP_ENDPOINT') || optional('VITE_OTEL_OTLP_ENDPOINT'),
    tracesEndpoint: optional('VITE_OTEL_EXPORTER_OTLP_TRACES_ENDPOINT'),
    metricsEndpoint: optional('VITE_OTEL_EXPORTER_OTLP_METRICS_ENDPOINT'),
    logsEndpoint: optional('VITE_OTEL_EXPORTER_OTLP_LOGS_ENDPOINT'),
    serviceName: optional('VITE_OTEL_SERVICE_NAME', __APP_NAME__),
    metricExportIntervalMs: optionalInt(
      'VITE_OTEL_METRIC_EXPORT_INTERVAL_MS',
      DEFAULT_OTEL_METRIC_EXPORT_INTERVAL_MS,
    ),
  },
} as const

export type Config = typeof config
